import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBulkDropdownData } from "../../api/keyvalue";
import { countryStartsWith } from "../../api/countryApi";
import SearchDropdown from "../../components/input/SearchDropdown";
import { Card, CardContent } from "@mui/material";
import { Compass, Search } from "react-feather";
import { FORMS, MODULES } from "../../const";
import Button from "../../components/input/Button";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Select } from "semantic-ui-react";
import Grid from "../../components/Grid";
import { StatusCellStyle, ActiveCellStyle } from "../../components/renderer/StyleRenderer";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useUserCompany from "../../hooks/useUserCompany";
import SideDrawer from "../../components/input/SideDrawer";
import Tab from "../../components/Tab";
import GSDivisionAddComponent from "../../components/master/GSDivisionAddComponent";
import {
  searchGSDivision,
  createGSDivision,
  updateGSDivision,
  getGSDivisionById,
} from "../../api/gsDivisionRequest";
import { toast } from "react-toastify";

const FORM = FORMS.GS_DIVISION.GS.form;

const GSDivision = () => {
  const userCompany = useUserCompany();
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.GS_DIVISION,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [searchStatus, setSearchStatus] = useState(null);
  const [searchActive, setSearchActive] = useState(null);
  const [searchCountry, setSearchCountry] = useState(null);
  const [searchFieldReset, setSearchFieldReset] = useState(false);

  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [editGSDivision, setEditGSDivision] = useState(null);
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // Dropdown options
  const [countryOptions, setCountryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);

  const gsDivisionRef = useRef();

  // Search input refs
  const gsDivisionCodeRef = useRef(null);
  const gsDivisionNameRef = useRef(null);

  const user = useSelector((state) => state.user);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setIsSearchLoading(true);

      try {
        const { status, active } = await getBulkDropdownData([
          { key: "status" },
          { key: "active" },
        ]);
        setStatusOptions(status || []);
        setActiveOptions(active || []);
      } catch (err) {
        console.error(
          "Error loading dropdown data for Grama Niladhari Division",
          err,
        );
      } finally {
        setIsLoading(false);
        setIsSearchLoading(false);
      }
    })();
  }, []);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditGSDivision(null);
    setDrawerTab(1);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const reset = () => {
    if (gsDivisionRef.current) {
      gsDivisionRef.current.resetForm();
    }
    setEditMode(false);
    setEditGSDivision(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsSubmitLoading(true);

    const formErrors = gsDivisionRef.current
      ? gsDivisionRef.current.validateForm()
      : {};
    if (Object.keys(formErrors).length > 0) {
      setIsSubmitLoading(false);
      toast.error("Please fill all required fields correctly.", {
        autoClose: 5000,
        containerId: "Main",
      });
    } else {
      const formData = gsDivisionRef.current.getData();
      try {
        if (editMode) {
          await updateGSDivision({
            ...formData,
            rgsId: editGSDivision?.rgsId,
            author: user.username,
          });
          toast.success("Grama Niladhari Division updated successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          await createGSDivision({
            ...formData,
            author: user.username,
          });
          toast.success("Grama Niladhari Division added successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        handleDrawerClose();
        reset();
        if (searchClicked) {
          await fetchSearch();
        }
      } catch (error) {
        console.error(error);
        if (error.response?.status === 409) {
          if (gsDivisionRef.current?.setDuplicateError) {
            gsDivisionRef.current.setDuplicateError("A record with this name already exists.");
          }
          toast.error("A record with this name already exists.", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          toast.error(
            "An error occurred while saving the Grama Niladhari Division.",
            {
              autoClose: 5000,
              containerId: "Main",
            },
          );
        }
      } finally {
        setIsSubmitLoading(false);
      }
    }
  };

  const fetchSearch = async () => {
    setSearchSubmitLoading(true);
    try {
      const response = await searchGSDivision({
        rgsCode: gsDivisionCodeRef.current?.value || "",
        rgsName: gsDivisionNameRef.current?.value || "",
        rgsCountry: searchCountry,
        rgsStatus: searchStatus,
        rgsActive: searchActive,
        rgsComId: userCompany?.comId || null,
      });
      setSearchRes(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error(error);
      setSearchRes([]);
    } finally {
      setSearchSubmitLoading(false);
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(false);
    setActiveAccordion1(false);
    setResetVisible(true);
    setSearchClicked(true);
    await fetchSearch();
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    if (gsDivisionCodeRef.current) gsDivisionCodeRef.current.value = "";
    if (gsDivisionNameRef.current) gsDivisionNameRef.current.value = "";
    setSearchCountry(null);
    setSearchStatus(null);
    setSearchActive(null);
    setSearchFieldReset(true);
  };

  return (
    <>
      <Card className="trailers-card-container">
        <CardContent>
          <div
            className="ui attached tabular"
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div className="ui tabular menu attached">
              <div
                className={`item ${tab === 1 ? "active" : ""}`}
                onClick={() => {
                  setTab(1);
                }}
              >
                <span>
                  <Compass size={15} style={{ marginRight: "5px" }} />
                </span>
                Grama Niladhari Division Search
              </div>
            </div>
            <Button
              template="drawerOpen"
              onClick={handleDrawerOpen}
              authenticated={true}
              form={FORM}
              module={MODULES.Master}
              disabled={firstViableTab === -1}
              editMode={false}
            />
          </div>
          <div
            className="ui bottom attached active tab segment"
            style={{
              display: tab === 1 ? "block" : "none",
              overflow: "visible",
            }}
          >
            <div
              className="ui styled accordion"
              style={{ width: "100%", overflow: "visible" }}
            >
              <div
                className={`title ${activeAccordion1 ? "active" : ""}`}
                onClick={() => setActiveAccordion1(!activeAccordion1)}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span>Search</span>
                    <Search size={14} style={{ marginLeft: "10px" }} />
                    <i className="dropdown icon"></i>
                  </div>

                  <i
                    className="refresh icon primary button"
                    style={{
                      display: `${resetVisible ? "flex" : "none"}`,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      handleSearchReset(e);
                    }}
                  ></i>
                </div>
              </div>
              <div
                className={`content ${activeAccordion1 ? "active" : ""}`}
                style={{ overflow: "visible" }}
              >
                {isSearchLoading ? (
                  <LoadingSpinner />
                ) : (
                  <form className="ui form">
                    <h4 className="ui dividing header">
                      Grama Niladhari Division Search Parameters
                    </h4>
                    <div className="two fields">
                      <div className="field">
                        <label>Grama Niladhari Division Code</label>
                        <input
                          type="text"
                          placeholder="Grama Niladhari Division Code"
                          name="rgsCode"
                          ref={gsDivisionCodeRef}
                        />
                      </div>

                      <div className="field">
                        <label>Grama Niladhari Division Name</label>
                        <input
                          type="text"
                          placeholder="Grama Niladhari Division Name"
                          name="rgsName"
                          ref={gsDivisionNameRef}
                        />
                      </div>
                    </div>
                    <div className="three fields">
                      <SearchDropdown
                        key={1}
                        editMode={false}
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        clearable={true}
                        onClear={() => {
                          setSearchCountry(null);
                        }}
                        onResultSelect={(data) => setSearchCountry(data)}
                        api={countryStartsWith}
                        label="Country"
                      />
                      <div className="field">
                        <label>Status</label>
                        <Select
                          options={statusOptions}
                          placeholder="Select Status"
                          clearable
                          value={searchStatus}
                          style={{ height: "10px" }}
                          onChange={(e, data) => {
                            setSearchStatus(
                              data.value === "" ? null : data.value,
                            );
                          }}
                        />
                      </div>
                      <div className="field">
                        <label>Active</label>
                        <Select
                          options={activeOptions}
                          placeholder="Select Active"
                          clearable
                          value={searchActive}
                          style={{ height: "10px" }}
                          onChange={(e, data) => {
                            setSearchActive(
                              data.value === "" ? null : data.value,
                            );
                          }}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <div className="field" style={{ width: "100%" }}>
                        <Button
                          template="search"
                          onClick={(e) => {
                            handleSearchSubmit(e);
                          }}
                        />
                        <Button
                          template="searchReset"
                          onClick={(e) => {
                            handleSearchReset(e);
                          }}
                        />
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
            {searchClicked && (
              <>
                {searchSubmitLoading ? (
                  <LoadingSpinner />
                ) : (
                  <Grid
                    isLoading={searchFetchLoading}
                    content={searchRes}
                    config={[
                      {
                        headerName: "Id",
                        field: "rgsId",
                        sort: "desc",
                        hide: true,
                      },
                      {
                        headerName: "Grama Niladhari Division Code",
                        field: "rgsCode",
                      },
                      {
                        headerName: "Grama Niladhari Division Name",
                        field: "rgsName",
                      },
                      {
                        headerName: "Country",
                        field: "couCountry",
                      },
                      {
                        headerName: "Company",
                        field: "rgsCompanyName",
                      },
                      {
                        headerName: "Description",
                        field: "rgsDescription",
                      },
                      {
                        headerName: "Status",
                        field: "statusName",
                        cellRenderer: StatusCellStyle,
                      },
                      {
                        headerName: "Active",
                        field: "activeName",
                        cellRenderer: ActiveCellStyle,
                      },
                    ]}
                    onCellDoubleClicked={async (props) => {
                      setSearchFetchLoading(true);
                      try {
                        const response = await getGSDivisionById(
                          props.data.rgsId,
                        );
                        setEditGSDivision(response || props.data);
                        setEditMode(true);
                        setIsDrawerOpen(true);
                      } catch (err) {
                        console.error(err);
                      } finally {
                        setSearchFetchLoading(false);
                      }
                    }}
                  />
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div
              className="trailer-code-header"
              style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
            >
              <h3>
                Grama Niladhari Division Code: {editGSDivision?.rgsCode}
              </h3>
            </div>
          ) : null
        }
        body={
          <form className="drawer-content" onSubmit={handleSubmit}>
            <div style={{ width: "60vw", boxShadow: "none" }}>
              <div className="ui top attached tabular menu">
                <Tab
                  Icon={Compass}
                  onClick={() => {
                    setDrawerTab(1);
                  }}
                  active={drawerTab === 1}
                  label="Grama Niladhari Division"
                  authenticated={true}
                  form={FORMS.GS_DIVISION.GS_DETAIL.form}
                  showMandatory={true}
                  isMandatory={true}
                />
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: drawerTab === 1 ? "block" : "none" }}
              >
                <GSDivisionAddComponent
                  ref={gsDivisionRef}
                  isLoading={isLoading}
                  dropdown={{
                    statusOptions,
                    activeOptions,
                  }}
                  editMode={editMode}
                  currentValues={editMode ? editGSDivision : null}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    type="submit"
                    template="create"
                    isLoading={isSubmitLoading}
                    create={!editMode}
                    form={FORM}
                    module={MODULES.Master}
                    section={FORMS.GS_DIVISION}
                    authenticated={true}
                    editMode={editMode}
                  />
                </div>
              </div>
            </div>
          </form>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default GSDivision;
